import { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import HomeIcon from '@mui/icons-material/Home';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

import theme from './theme';
import HomeScreen from './screens/HomeScreen';
import InsightsScreen from './screens/InsightsScreen';
import EditSparkScreen from './screens/EditSparkScreen';
import WelcomeModal from './components/WelcomeModal';

const WELCOME_SEEN_KEY = 'bellow_welcome_seen';

const DARK_BG = '#1a1a2e';
const CARD_BG = '#fffeed';
const NAV_SELECTED = '#ff371d';
const NAV_EASING = 'cubic-bezier(0.2, 0, 0, 1)';




function Header({ onLogoClick }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        flexShrink: 0,
      }}
    >
      <ButtonBase
        onClick={onLogoClick}
        sx={{
          borderRadius: 2,
          px: 1,
          py: 0.5,
          ml: -1,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: '"Roboto Flex", sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(16px, 5vw, 22px)',
            color: CARD_BG,
            textTransform: 'uppercase',
            lineHeight: 1,
            fontVariationSettings: "'GRAD' 0, 'XOPQ' 96, 'XTRA' 468, 'YOPQ' 79, 'YTAS' 750, 'YTDE' -203, 'YTFI' 738, 'YTLC' 514, 'YTUC' 712, 'wdth' 151",
          }}
        >
          Bellow
        </Typography>
      </ButtonBase>
    </Box>
  );
}

function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const homeActive = location.pathname === '/';
  const insightsActive = location.pathname === '/patterns';

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 1,
        py: 2,
        zIndex: 10,
        pointerEvents: 'none',
      }}
    >
      <ButtonBase
        onClick={() => navigate('/')}
        disableRipple
        sx={{
          width: 72,
          height: 56,
          borderRadius: homeActive ? '16px' : '100px',
          bgcolor: homeActive ? NAV_SELECTED : 'rgba(195, 195, 212, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: `border-radius 300ms ${NAV_EASING}, background-color 300ms ${NAV_EASING}`,
          overflow: 'hidden',
          pointerEvents: 'auto',
          backdropFilter: 'blur(8px)',
          '&:hover': { bgcolor: homeActive ? NAV_SELECTED : 'rgba(195, 195, 212, 0.7)' },
        }}
      >
        <HomeIcon sx={{ color: homeActive ? '#fff' : DARK_BG, fontSize: 24 }} />
      </ButtonBase>

      <ButtonBase
        onClick={() => navigate('/patterns')}
        disableRipple
        sx={{
          width: 72,
          height: 56,
          borderRadius: insightsActive ? '16px' : '100px',
          bgcolor: insightsActive ? NAV_SELECTED : 'rgba(195, 195, 212, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: `border-radius 300ms ${NAV_EASING}, background-color 300ms ${NAV_EASING}`,
          overflow: 'hidden',
          pointerEvents: 'auto',
          backdropFilter: 'blur(8px)',
          '&:hover': { bgcolor: insightsActive ? NAV_SELECTED : 'rgba(195, 195, 212, 0.7)' },
        }}
      >
        <FormatQuoteIcon sx={{ color: insightsActive ? '#fff' : DARK_BG, fontSize: 24 }} />
      </ButtonBase>
    </Box>
  );
}

/**
 * Side-by-side slider layout.
 *
 * Both screens live permanently in a 200%-wide flex row. A CSS translateX
 * slides the track left/right based on the current URL.
 *
 * The star illustration lives once in the track — not in each panel — so it
 * spans both screens as a single continuous graphic with no seam. The track
 * is 200vw wide, so 50% of it = 100vw (the insights panel's left edge).
 * The star is placed at left: calc(50% - 149px) so it sits at the same
 * position relative to insights as the Figma design specifies (-149px from
 * the panel's left edge). The home panel sees the arm that pokes left of
 * the star's own bounding box.
 */
const SWIPE_THRESHOLD = 60; // px needed to commit a swipe

function SliderLayout({ onLogoClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isInsights = location.pathname === '/patterns';

  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartRef = useRef(null);
  const directionRef = useRef(null); // 'horizontal' | 'vertical' | null
  const trackRef = useRef(null);


  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    directionRef.current = null;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;

    // Lock swipe direction once movement is clear
    if (!directionRef.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      directionRef.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }

    if (directionRef.current === 'horizontal') {
      e.preventDefault(); // block page scroll during horizontal swipe
      // Resist dragging past the edge screens
      const resistance = 0.35;
      let clamped = dx;
      if (!isInsights && dx > 0) clamped = dx * resistance; // resist rightward on home
      if (isInsights && dx < 0) clamped = dx * resistance;  // resist leftward on patterns
      setDragX(clamped);
      setIsDragging(true);
    }
  }, [isInsights]);

  const handleTouchEnd = useCallback(() => {
    if (directionRef.current === 'horizontal') {
      if (!isInsights && dragX < -SWIPE_THRESHOLD) {
        navigate('/patterns');
      } else if (isInsights && dragX > SWIPE_THRESHOLD) {
        navigate('/');
      }
    }
    setDragX(0);
    setIsDragging(false);
    touchStartRef.current = null;
    directionRef.current = null;
  }, [isInsights, dragX, navigate]);

  // Register touchmove as non-passive so preventDefault works
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, [handleTouchMove]);

  const basePercent = isInsights ? -50 : 0;
  const transform = `translateX(calc(${basePercent}% + ${dragX}px))`;
  const transition = isDragging ? 'none' : `transform 420ms ${NAV_EASING}`;

  return (
    <Box
      sx={{
        height: '100dvh',
        bgcolor: DARK_BG,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Header onLogoClick={onLogoClick} />

      {/* Sliding track */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <Box
          ref={trackRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            display: 'flex',
            width: '200%',
            height: '100%',
            transform,
            transition,
            willChange: 'transform',
            position: 'relative',
          }}
        >

          {/* ── Home panel ── */}
          <Box
            sx={{
              width: '50%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                pb: '88px',
              }}
            >
              <HomeScreen />
            </Box>
          </Box>

          {/* ── Insights panel ── */}
          <Box
            sx={{
              width: '50%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                pb: '88px',
              }}
            >
              <InsightsScreen />
            </Box>
          </Box>
        </Box>
      </Box>

      <BottomNav />
    </Box>
  );

}

function AppContent({ onLogoClick }) {
  return (
    <Routes>
      {/* Full-screen edit screens take over the whole viewport */}
      <Route path="/spark/:id" element={<EditSparkScreen />} />
      <Route path="/edit/:id" element={<EditSparkScreen />} />
      {/* Everything else renders inside the slider (catches / and /patterns) */}
      <Route path="*" element={<SliderLayout onLogoClick={onLogoClick} />} />
    </Routes>
  );
}

function App() {
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(WELCOME_SEEN_KEY)) {
      setWelcomeOpen(true);
    }
  }, []);

  const handleCloseWelcome = () => {
    setWelcomeOpen(false);
    localStorage.setItem(WELCOME_SEEN_KEY, 'true');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent onLogoClick={() => setWelcomeOpen(true)} />
        <WelcomeModal open={welcomeOpen} onClose={handleCloseWelcome} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
